/**
 * useConnectSpaces — list + detail queries for Ando Connect Spaces.
 *
 * Mirrors the ``features/share`` hook style (TanStack Query, one query key
 * factory, 30s cadence) so the Connect hub + detail shells share the same
 * cache semantics as the rest of the workspace surfaces.
 *
 * BE surface: ``ando-agent/src/api/v1/endpoints/connect.py`` via the
 * ``@ando/core`` ``connect`` endpoint helpers (``listSpaces`` / ``getSpace``).
 *
 * ``GET /connect/spaces`` (``list_my_spaces``) returns ONLY spaces the caller
 * OWNS, each carrying a ``summary: { member_count, grant_counts }`` block.
 * ``GET /connect/spaces/shared`` (``list_shared_with_me``) returns the spaces
 * the caller is an ACTIVE member of in OTHER orgs — the "shared with me"
 * counterpart, surfaced via ``useSharedConnectSpaces`` below. Both feed the
 * hub from real BE data — nothing is fabricated client-side.
 *
 * ``summaryOf`` reads the nested ``summary`` block defensively (the TS wire
 * type allows extra keys) so callers don't have to reach into ``unknown``.
 */

import {
  type ConnectGrantableConnection,
  type ConnectSharedSpace,
  type ConnectSpace,
  type ConnectSpaceDetail,
  getSpace,
  listGrantableConnections,
  listSharedSpaces,
  listSpaces,
} from "@ando/core";
import { queryOptions, useQuery } from "@tanstack/react-query";

export const connectQueryKey = {
  /** All keys under the Connect feature — single invalidation root. */
  all: ["connect-spaces"] as const,
  list: () => ["connect-spaces", "list"] as const,
  shared: () => ["connect-spaces", "shared"] as const,
  detail: (id: string) => ["connect-spaces", "detail", id] as const,
  grantableConnections: () => ["connect-spaces", "grantable-connections"] as const,
};

export interface UseConnectSpacesResult {
  readonly data: ConnectSpace[] | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => Promise<unknown>;
}

/** Shared list query — hook + `/workspace/connect` route loader (intent preload). */
export const connectSpacesListQueryOptions = () =>
  queryOptions({
    queryKey: connectQueryKey.list(),
    queryFn: () => listSpaces(),
    // 30s — same cadence as documents / canvases / shares. Member counts
    // and last-activity tick over server-side as guests interact.
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

export function useConnectSpaces(
  options: { readonly enabled?: boolean } = {},
): UseConnectSpacesResult {
  const query = useQuery({
    ...connectSpacesListQueryOptions(),
    enabled: options.enabled ?? true,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  };
}

export interface UseSharedConnectSpacesResult {
  readonly data: ConnectSharedSpace[] | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => Promise<unknown>;
}

/**
 * "Condivisi con me" — Spaces the caller is an active member of in OTHER orgs
 * (``GET /connect/spaces/shared``). Same 30s cadence + cache semantics as the
 * owned ``useConnectSpaces`` so the two hub sections tick over together.
 */
export function useSharedConnectSpaces(
  options: { readonly enabled?: boolean } = {},
): UseSharedConnectSpacesResult {
  const query = useQuery({
    queryKey: connectQueryKey.shared(),
    queryFn: () => listSharedSpaces(),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  };
}

/** Per-space summary block returned alongside each list row. */
export interface ConnectSpaceSummary {
  readonly member_count: number;
  readonly grant_counts: Readonly<Record<string, number>>;
}

const EMPTY_SUMMARY: ConnectSpaceSummary = { member_count: 0, grant_counts: {} };

/**
 * Read the nested ``summary`` block off a list row defensively. The wire
 * type allows extra keys (``[key: string]: unknown``); the BE nests counts
 * under ``summary`` (``list_my_spaces``) rather than flat fields.
 */
export function summaryOf(space: ConnectSpace): ConnectSpaceSummary {
  const raw = (space as { summary?: unknown }).summary;
  if (raw && typeof raw === "object") {
    const s = raw as Partial<ConnectSpaceSummary>;
    return {
      member_count: typeof s.member_count === "number" ? s.member_count : 0,
      grant_counts: s.grant_counts && typeof s.grant_counts === "object" ? s.grant_counts : {},
    };
  }
  return EMPTY_SUMMARY;
}

export interface UseConnectSpaceResult {
  readonly data: ConnectSpaceDetail | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly refetch: () => Promise<unknown>;
}

export function useConnectSpace(spaceId: string | undefined): UseConnectSpaceResult {
  const query = useQuery({
    queryKey: connectQueryKey.detail(spaceId ?? "__none__"),
    queryFn: () => getSpace(spaceId as string),
    enabled: Boolean(spaceId),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => query.refetch(),
  };
}

export interface UseGrantableConnectionsResult {
  readonly connections: readonly ConnectGrantableConnection[];
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * The structured-data connections an owner may grant into a Space, read-only
 * (``GET /connect/grantable-connections``). Connector grants are
 * CONNECTION-BASED: the grant form renders its picker from THIS server list —
 * ``connection_id`` is the exact id the BE accepts on submit, so the offered
 * options can never drift from the BE's fail-closed grantable set. Cached
 * longer than the live surfaces: the set is effectively static within a
 * session.
 */
export function useGrantableConnections(
  options: { readonly enabled?: boolean } = {},
): UseGrantableConnectionsResult {
  const query = useQuery({
    queryKey: connectQueryKey.grantableConnections(),
    queryFn: () => listGrantableConnections(),
    enabled: options.enabled ?? true,
    staleTime: 5 * 60_000,
  });
  return {
    connections: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
